/**
 * Configuração Central de Dropdowns - SmartSelect
 *
 * Este arquivo define como cada entidade deve ser carregada em dropdowns:
 * - strategy: 'server' | 'client' | 'auto'
 * - Configurações de RPC, campos de busca, mapeamento de labels
 *
 * REGRA: Tabelas com >500 registros devem usar 'server' ou 'auto'
 *
 * @see SmartSelect.jsx - Componente que usa esta configuração
 * @see genericServerSearch.js - Fallback para entidades sem RPC
 */

// =============================================================================
// TYPES
// =============================================================================

/** Strategy for loading dropdown data */
export type SearchStrategy = 'server' | 'client' | 'auto';

/** Filter operator for defaultFilters */
export type DefaultFilterOperator = 'eq' | 'neq' | 'is';

/** Single default filter applied to the query */
export interface EntityDefaultFilter {
  column: string;
  operator: DefaultFilterOperator;
  value: unknown;
}

/** Signed-URL resolution config for image fields in results */
export interface EntitySignedUrlConfig {
  field: string;
  urlField: string;
  bucket: string;
}

/** Row shape passed to mapResult/labelFn (loose, entity-dependent) */
export type EntityRow = Record<string, unknown>;

/** Options passed to rpcParams builder */
export type EntityRpcOptions = Record<string, unknown> & {
  limit?: number;
  recurso?: string;
  obra_id?: string | null;
};

/** Mapped dropdown result shape (at minimum carries value + label) */
export interface EntityMappedResult extends EntityRow {
  value: unknown;
  label: string;
}

/** labelFn may be a field name (string) or a row->string function */
export type EntityLabelFn = string | ((row: EntityRow) => string);

/**
 * Configuration shape for a single entity in ENTITY_CONFIG.
 * Fields are intentionally optional so that each entity only declares what
 * it needs (server-only entities skip client fields, etc.).
 */
export interface EntityConfig {
  strategy: SearchStrategy;
  /** Threshold for 'auto' strategy (overrides ROW_COUNT_THRESHOLD) */
  threshold?: number;

  // --- Server-side (RPC) ---
  rpc?: string;
  rpcParams?: (query: string, options?: EntityRpcOptions) => Record<string, unknown>;
  mapResult?: (row: EntityRow) => EntityMappedResult;
  generateSignedUrls?: EntitySignedUrlConfig;

  // --- Table fallback / client-side ---
  table?: string;
  select?: string;
  orderBy?: string;
  labelFn?: EntityLabelFn;
  searchFields?: string[];
  defaultFilters?: EntityDefaultFilter[];
  /**
   * Extra PostgREST filters derived from SmartSelect `entityOptions`
   * (e.g. `{ obra_id }` → filter ambientes of that obra).
   */
  optionFilters?: (options: EntityRpcOptions) => EntityDefaultFilter[];
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Threshold para decisão automática: acima disso, usa server-side */
export const ROW_COUNT_THRESHOLD = 500;

/** TTL do cache de contagem de registros (5 minutos) */
export const COUNT_CACHE_TTL_MS = 5 * 60 * 1000;

/** Limite padrão de resultados em buscas server-side */
export const DEFAULT_SEARCH_LIMIT = 50;

// =============================================================================
// CONFIGURAÇÃO DE ENTIDADES
// =============================================================================

/**
 * Configuração de cada entidade para o SmartSelect
 *
 * Estrutura:
 * - strategy: 'server' | 'client' | 'auto'
 *   - server: sempre busca no servidor (tabelas grandes)
 *   - client: sempre carrega tudo client-side (tabelas pequenas)
 *   - auto: decide baseado em contagem de registros
 *
 * - rpc: nome da função RPC para busca server-side (opcional)
 * - rpcParams: função que retorna parâmetros para a RPC
 * - mapResult: função que mapeia resultado da RPC para {value, label, ...}
 *
 * - table: nome da tabela (para fallback genérico ou client-side)
 * - select: campos a selecionar
 * - orderBy: campo de ordenação
 * - labelFn: string (campo) ou função para criar o label
 * - searchFields: campos para busca (usado no fallback genérico)
 * - defaultFilters: filtros sempre aplicados
 */
export const ENTITY_CONFIG: Record<string, EntityConfig> = {
  // ===========================================================================
  // TABELAS GRANDES (>2000) - SEMPRE SERVER-SIDE
  // ===========================================================================

  produto: {
    strategy: 'server',
    rpc: 'fn_search_produtos',
    rpcParams: (query: string, options: EntityRpcOptions = {}) => ({
      p_search: query || '',
      p_limit: options.limit || DEFAULT_SEARCH_LIMIT
    }),
    mapResult: (row: EntityRow) => ({
      value: row.id,
      label: [row.marca_nome, row.modelo, row.nome_fantasia].filter(Boolean).join(' - ') || String(row.id ?? ''),
      // Dados extras para renderização customizada
      marca_nome: row.marca_nome,
      modelo: row.modelo,
      nome_fantasia: row.nome_fantasia,
      valor_venda: row.valor_venda,
      cmv: row.cmv,
      foto_1: row.foto_1,
      foto_1_url: null // preenchido por genericServerSearch via generateSignedUrls
    }),
    // Gerar signed URLs para fotos após mapResult
    generateSignedUrls: { field: 'foto_1', urlField: 'foto_1_url', bucket: 'documento' },
    // Fallback se RPC falhar
    table: 'produto',
    select: 'id, cod, modelo, nome_fantasia, valor_venda, cmv, foto_1, marca:marca_id(id, nome)',
    orderBy: 'modelo',
    searchFields: ['modelo', 'nome_fantasia', 'cod'],
    defaultFilters: [{ column: 'is_ativo', operator: 'eq', value: true }]
  },

  cidade: {
    strategy: 'server',
    rpc: 'fn_search_cidades',
    rpcParams: (query: string, options: EntityRpcOptions = {}) => ({
      p_search: query || '',
      p_limit: options.limit || DEFAULT_SEARCH_LIMIT
    }),
    mapResult: (row: EntityRow) => ({
      value: row.id,
      label: row.uf ? `${row.cidade} / ${row.uf}` : String(row.cidade ?? ''),
      cidade: row.cidade,
      uf: row.uf,
      estado_id: row.estado_id
    }),
    // Fallback se RPC não existir ainda
    table: 'cidade',
    select: 'id, cidade, cod, estado_id, estado:estado_id(id, uf)',
    orderBy: 'cidade',
    labelFn: (row: EntityRow) => {
      const estado = row.estado as { uf?: string } | undefined;
      return estado?.uf ? `${row.cidade} / ${estado.uf}` : String(row.cidade ?? '');
    },
    searchFields: ['cidade']
  },

  obra_ponto: {
    strategy: 'server',
    rpc: 'fn_search_obra_pontos',
    rpcParams: (query: string, options: EntityRpcOptions = {}) => ({
      p_recurso: options.recurso || 'OBRA_PRODUTO',
      p_query: query || '',
      p_limit: options.limit || DEFAULT_SEARCH_LIMIT,
      p_obra_id: options.obra_id || null
    }),
    mapResult: (row: EntityRow) => ({
      value: row.id,
      label: `${row.ponto} - ${row.ambiente} - ${row.area || ''} - ${row.obra}`,
      ponto: row.ponto,
      ambiente: row.ambiente,
      area: row.area,
      area_id: row.area_id,
      obra: row.obra,
      obra_id: row.obra_id,
      obra_ambiente_id: row.obra_ambiente_id,
      etapa_compra_id: row.etapa_compra_id,
      etapa_instalacao_id: row.etapa_instalacao_id
    })
  },

  os: {
    strategy: 'server',
    // TODO: Criar fn_search_os se necessário
    table: 'os',
    select: 'id, cod, titulo, obra:obra_id(id, obra)',
    orderBy: 'cod',
    labelFn: (row: EntityRow) => {
      const obra = row.obra as { obra?: string } | undefined;
      return obra?.obra ? `${row.cod} - ${obra.obra}` : String(row.cod ?? '');
    },
    searchFields: ['cod', 'titulo'],
    defaultFilters: [{ column: 'deleted_at', operator: 'is', value: null }]
  },

  requisicao: {
    strategy: 'server',
    // TODO: Criar fn_search_requisicoes se necessário
    table: 'requisicao',
    select: 'id, cod, descricao, obra:obra_id(id, obra)',
    orderBy: 'cod',
    labelFn: (row: EntityRow) => {
      const obra = row.obra as { obra?: string } | undefined;
      return obra?.obra ? `${row.cod} - ${obra.obra}` : String(row.cod ?? '');
    },
    searchFields: ['cod', 'descricao'],
    defaultFilters: [{ column: 'deleted_at', operator: 'is', value: null }]
  },

  // ===========================================================================
  // TABELAS MÉDIAS (500-2000) - AUTO (DECIDE POR CONTAGEM)
  // ===========================================================================

  pessoa: {
    strategy: 'auto',
    threshold: 1500,
    table: 'pessoa',
    select: 'id, apelido, nome, email',
    orderBy: 'apelido',
    labelFn: 'apelido',
    searchFields: ['apelido', 'nome', 'email'],
    defaultFilters: [{ column: 'deleted_at', operator: 'is', value: null }]
  },

  obra: {
    strategy: 'auto',
    threshold: 1500,
    table: 'obra',
    select: 'id, obra, cod, obra_status_id',
    orderBy: 'obra',
    labelFn: 'obra',
    searchFields: ['obra', 'cod'],
    defaultFilters: [{ column: 'deleted_at', operator: 'is', value: null }]
  },

  proposta: {
    strategy: 'auto',
    threshold: 1500,
    table: 'proposta',
    // `oportunidade.titulo`, não `nome`: a coluna nome não existe, e o embed
    // devolvia 42703 ("column oportunidade_1.nome does not exist"), deixando o
    // seletor sempre vazio. Passou despercebido porque não havia consumidor de
    // entity="proposta" até o recebimento de venda (JOSH-11).
    select: 'id, cod, oportunidade:oportunidade_id(id, titulo)',
    orderBy: 'cod',
    labelFn: (row: EntityRow) => {
      const oportunidade = row.oportunidade as { titulo?: string } | undefined;
      return oportunidade?.titulo ? `${row.cod} - ${oportunidade.titulo}` : String(row.cod ?? '');
    },
    searchFields: ['cod'],
    defaultFilters: [{ column: 'deleted_at', operator: 'is', value: null }]
  },

  oportunidade: {
    strategy: 'auto',
    threshold: 1500,
    table: 'oportunidade',
    select: 'id, nome, cliente:pessoa_id(id, apelido)',
    orderBy: 'nome',
    labelFn: (row: EntityRow) => {
      const cliente = row.cliente as { apelido?: string } | undefined;
      return cliente?.apelido ? `${row.nome} - ${cliente.apelido}` : String(row.nome ?? '');
    },
    searchFields: ['nome'],
    defaultFilters: [{ column: 'deleted_at', operator: 'is', value: null }]
  },

  obra_ambiente: {
    strategy: 'server',
    table: 'obra_ambiente',
    select: 'id, ambiente, obra_area:obra_area_id!inner(id, area, obra:obra_id(id, obra))',
    orderBy: 'ambiente',
    labelFn: (row: EntityRow) => {
      const area = row.obra_area as { area?: string; obra?: { obra?: string } } | undefined;
      const ambiente = String(row.ambiente ?? '');
      const obraNome = area?.obra?.obra;
      return obraNome ? `${ambiente} - ${obraNome}` : ambiente;
    },
    searchFields: ['ambiente'],
    defaultFilters: [{ column: 'deleted_at', operator: 'is', value: null }],
    optionFilters: (options: EntityRpcOptions) => {
      if (!options.obra_id) return [];
      return [{ column: 'obra_area.obra_id', operator: 'eq', value: options.obra_id }];
    },
  },

  // ===========================================================================
  // TABELAS PEQUENAS (<500) - SEMPRE CLIENT-SIDE
  // ===========================================================================

  estado: {
    strategy: 'client',
    table: 'estado',
    select: 'id, uf, nome',
    orderBy: 'nome',
    labelFn: (row: EntityRow) => `${row.uf} - ${row.nome}`,
    searchFields: ['uf', 'nome']
  },

  obra_status: {
    strategy: 'client',
    table: 'obra_status',
    select: 'id, status, cor_br, cor_texto',
    orderBy: 'status',
    labelFn: 'status'
  },

  obra_etapa: {
    strategy: 'client',
    table: 'obra_etapa',
    select: 'id, etapa, ordem',
    orderBy: 'ordem',
    labelFn: 'etapa'
  },

  obra_produto_status: {
    strategy: 'client',
    table: 'obra_produto_status',
    select: 'id, obra_produto_status, cor_br, cor_texto, ordem',
    orderBy: 'ordem',
    labelFn: 'obra_produto_status'
  },

  os_status: {
    strategy: 'client',
    table: 'os_status',
    select: 'id, os_status, cor_br, cor_texto',
    orderBy: 'ordem',
    labelFn: 'os_status'
  },

  requisicao_status: {
    strategy: 'client',
    table: 'requisicao_status',
    select: 'id, requisicao_status, cor_br, cor_texto',
    orderBy: 'ordem',
    labelFn: 'requisicao_status'
  },

  ambiente_tipo: {
    strategy: 'client',
    table: 'ambiente_tipo',
    select: 'id, ambiente_tipo',
    orderBy: 'ambiente_tipo',
    labelFn: 'ambiente_tipo'
  },

  ponto_tipo: {
    strategy: 'client',
    table: 'ponto_tipo',
    select: 'id, ponto_tipo, prefixo',
    orderBy: 'ponto_tipo',
    labelFn: 'ponto_tipo'
  },

  marca: {
    strategy: 'client',
    table: 'marca',
    select: 'id, nome',
    orderBy: 'nome',
    labelFn: 'nome'
  },

  fornecedor: {
    strategy: 'client',
    table: 'fornecedor',
    select: 'id, nome, cnpj',
    orderBy: 'nome',
    labelFn: 'nome',
    searchFields: ['nome', 'cnpj']
  },

  transportadora: {
    strategy: 'client',
    table: 'transportadora',
    select: 'id, transportadora',
    orderBy: 'transportadora',
    labelFn: 'transportadora',
    searchFields: ['transportadora']
  },

  filial: {
    strategy: 'client',
    table: 'filial',
    select: 'id, filial',
    orderBy: 'filial',
    labelFn: 'filial'
  },

  departamento: {
    strategy: 'client',
    table: 'departamento',
    select: 'id, departamento',
    orderBy: 'departamento',
    labelFn: 'departamento'
  },

  cargo: {
    strategy: 'client',
    table: 'cargo',
    select: 'id, cargo',
    orderBy: 'cargo',
    labelFn: 'cargo'
  },

  conta: {
    strategy: 'client',
    table: 'conta',
    select: 'id, conta, tipo',
    orderBy: 'conta',
    labelFn: 'conta'
  },

  centro_custo: {
    strategy: 'client',
    table: 'centro_custo',
    select: 'id, centro_custo, codigo',
    orderBy: 'centro_custo',
    labelFn: (row: EntityRow) =>
      row.codigo ? `${row.codigo} - ${row.centro_custo}` : String(row.centro_custo ?? '')
  },

  categoria_contabil: {
    strategy: 'client',
    table: 'categoria_contabil',
    select: 'id, categoria_contabil, codigo',
    orderBy: 'categoria_contabil',
    labelFn: (row: EntityRow) =>
      row.codigo ? `${row.codigo} - ${row.categoria_contabil}` : String(row.categoria_contabil ?? '')
  },

  vistoria_tipo: {
    strategy: 'client',
    table: 'vistoria_tipo',
    select: 'id, nome',
    orderBy: 'nome',
    labelFn: 'nome',
    defaultFilters: [
      { column: 'deleted_at', operator: 'is', value: null },
      { column: 'ativo', operator: 'eq', value: true }
    ]
  },

  atividade_tipo: {
    strategy: 'client',
    table: 'atividade_tipo',
    select: 'id, nome, cor, modalidade_padrao, sync_google, duracao_padrao_min',
    orderBy: 'nome',
    labelFn: 'nome',
    defaultFilters: [{ column: 'ativo', operator: 'eq', value: true }]
  },

  atividade_area: {
    strategy: 'client',
    table: 'atividade_area',
    select: 'id, nome, capacidade, cor, filial_id',
    orderBy: 'nome',
    labelFn: 'nome',
    defaultFilters: [{ column: 'ativo', operator: 'eq', value: true }]
  },

  servico_status: {
    strategy: 'client',
    table: 'servico_status',
    select: 'id, status',
    orderBy: 'status',
    labelFn: 'status'
  }
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Obtém a configuração de uma entidade
 * @param entity - Nome da entidade
 * @returns Configuração ou null se não existir
 */
export const getEntityConfig = (entity: string): EntityConfig | null => {
  return ENTITY_CONFIG[entity] || null;
};

/**
 * Verifica se uma entidade deve usar busca server-side
 * @param entity - Nome da entidade
 * @param rowCount - Contagem de registros (para modo 'auto')
 * @returns true se deve usar server-side
 */
export const shouldUseServerSide = (entity: string, rowCount: number | null = null): boolean => {
  const config = getEntityConfig(entity);
  if (!config) return false;

  if (config.strategy === 'server') return true;
  if (config.strategy === 'client') return false;

  // Auto: decide baseado em contagem
  if (config.strategy === 'auto') {
    const threshold = config.threshold || ROW_COUNT_THRESHOLD;
    if (rowCount !== null) {
      return rowCount > threshold;
    }
    // Se não tem contagem, assume client-side (seguro)
    return false;
  }

  return false;
};

/**
 * Lista de entidades conhecidamente grandes (para otimização)
 * Usada para decidir estratégia antes de ter a contagem
 */
export const KNOWN_LARGE_ENTITIES: string[] = [
  'produto',
  'cidade',
  'obra_ponto',
  'obra_ambiente',
  'os',
  'requisicao',
  'os_servico',
  'requisicao_conteudo'
];

/**
 * Verifica se entidade é conhecidamente grande
 * @param entity - Nome da entidade
 */
export const isKnownLargeEntity = (entity: string): boolean => {
  return KNOWN_LARGE_ENTITIES.includes(entity);
};
